import EasyStar from 'easystarjs';
import { HERB_LIBRARY, HERB_MAP, QUEST_HERB_ID, QUEST_HERB_COUNT } from '../data/herbs';
import yardMap from '../../map/yard.json';

const TILE_W = 128;
const TILE_H = 64;

interface MapData {
  width: number; height: number;
  layers: { name: string; data: number[] }[];
  tilesets: any[];
  herbs: { id: string; tx: number; ty: number; icon: string }[];
  npcs: { id: string; tx: number; ty: number; icon: string; name: string; dialog: string }[];
  playerStart: { tx: number; ty: number };
  blocks: number[][];
}

const MAP = yardMap as unknown as MapData;

export class GameScene extends Phaser.Scene {
  private isoGroup!: Phaser.GameObjects.Group;
  private player!: Phaser.GameObjects.Image;
  private herbsOnMap: Map<string, Phaser.GameObjects.Image> = new Map();
  private npcsOnMap: Map<string, Phaser.GameObjects.Image> = new Map();
  private pathfinder!: EasyStar.js;
  private isMoving = false;
  private inventory: string[] = [];
  private questDone = false;
  private uiContainer!: Phaser.GameObjects.Container;
  private dialogBox!: Phaser.GameObjects.Container;
  private inventoryPanel!: Phaser.GameObjects.Container;
  private herbBookPanel!: Phaser.GameObjects.Container;
  private questPanel!: Phaser.GameObjects.Container;
  private miniMap!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'GameScene' });
  }

  preload() {
    // 瓦片
    this.load.image('tile_stone', 'assets/tiles/stone.png');
    this.load.image('tile_dirt', 'assets/tiles/dirt.png');
    this.load.image('tile_grass', 'assets/tiles/grass.png');
    this.load.image('tile_flower', 'assets/tiles/flower.png');
    this.load.image('tile_step', 'assets/tiles/step.png');
    // 物件
    this.load.image('fence', 'assets/sprites/fence.png');
    this.load.image('cabinet', 'assets/sprites/cabinet2.png');
    this.load.image('building', 'assets/sprites/building.png');
    // 草药
    this.load.image('herb_gancao', 'assets/sprites/herb_gancao.png');
    this.load.image('herb_danggui', 'assets/sprites/herb_danggui.png');
    this.load.image('herb_jinyinhua', 'assets/sprites/herb_jinyinhua.png');
    this.load.image('herb_lingzhi', 'assets/sprites/herb_lingzhi.png');
    // 角色
    this.load.image('protagonist', 'assets/sprites/protagonist.png');
    this.load.image('patient', 'assets/sprites/patient.png');
    this.load.image('child', 'assets/sprites/child.png');
  }

  create() {
    this.isoGroup = this.add.group();
    this.buildMap();
    this.buildPathfinder();
    this.spawnPlayer();
    this.spawnHerbs();
    this.spawnNPCs();
    this.buildUI();
    this.setupInput();
    this.setupPostMessage();
  }

  // ---------- 等轴测坐标转换 ----------
  private isoToScreen(tx: number, ty: number) {
    const sx = (tx - ty) * (TILE_W / 2) + this.cameras.main.width / 2;
    const sy = (tx + ty) * (TILE_H / 2) + this.cameras.main.height / 4;
    return { x: sx, y: sy };
  }

  private screenToIso(sx: number, sy: number) {
    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height / 4;
    const dx = sx - cx;
    const dy = sy - cy;
    const tx = Math.round(dx / (TILE_W / 2) + dy / (TILE_H / 2)) / 2;
    const ty = Math.round(-dx / (TILE_W / 2) + dy / (TILE_H / 2)) / 2;
    return { tx: Math.round(tx), ty: Math.round(ty) };
  }

  // ---------- 地图构建 ----------
  private buildMap() {
    const ground = MAP.layers.find((l) => l.name === 'ground')!;
    const tileKeys = ['tile_stone', 'tile_dirt', 'tile_grass', 'tile_flower', 'tile_step'];
    for (let ty = 0; ty < MAP.height; ty++) {
      for (let tx = 0; tx < MAP.width; tx++) {
        const idx = ty * MAP.width + tx;
        const gid = ground.data[idx];
        if (gid > 0 && gid <= tileKeys.length) {
          const pos = this.isoToScreen(tx, ty);
          const tile = this.add.image(pos.x, pos.y, tileKeys[gid - 1]);
          tile.setOrigin(0.5, 0.5);
          tile.setDepth(pos.y);
          this.isoGroup.add(tile);
        }
      }
    }
    // 药圃栅栏（西侧边界）
    for (let ty = 2; ty <= 5; ty++) {
      const pos = this.isoToScreen(1, ty);
      const fence = this.add.image(pos.x - 40, pos.y - 20, 'fence');
      fence.setScale(0.35);
      fence.setDepth(pos.y + 1);
    }
    // 医馆主屋（北部中央）
    const bPos = this.isoToScreen(8, 1);
    const building = this.add.image(bPos.x, bPos.y - 60, 'building');
    building.setScale(0.55);
    building.setDepth(bPos.y + 3);
    // 药柜（主屋内）
    const cabPos = this.isoToScreen(8, 2);
    const cabinet = this.add.image(cabPos.x, cabPos.y - 30, 'cabinet');
    cabinet.setScale(0.35);
    cabinet.setDepth(cabPos.y + 4);
  }

  // ---------- 寻路 ----------
  private buildPathfinder() {
    const grid: number[][] = [];
    for (let ty = 0; ty < MAP.height; ty++) {
      const row: number[] = [];
      for (let tx = 0; tx < MAP.width; tx++) {
        const blocked = MAP.blocks.some(([bx, by]) => bx === tx && by === ty);
        row.push(blocked ? 1 : 0);
      }
      grid.push(row);
    }
    this.pathfinder = new EasyStar.js();
    this.pathfinder.setGrid(grid);
    this.pathfinder.setAcceptableTiles([0]);
    this.pathfinder.enableDiagonals();
  }

  // ---------- 角色 ----------
  private spawnPlayer() {
    const pos = this.isoToScreen(MAP.playerStart.tx, MAP.playerStart.ty);
    this.player = this.add.image(pos.x, pos.y - 20, 'protagonist');
    this.player.setScale(0.6);
    this.player.setDepth(pos.y + 10);
  }

  // ---------- 草药 ----------
  private spawnHerbs() {
    for (const h of MAP.herbs) {
      const pos = this.isoToScreen(h.tx, h.ty);
      const spr = this.add.image(pos.x, pos.y - 25, h.icon);
      spr.setScale(0.35);
      spr.setDepth(pos.y + 5);
      spr.setInteractive({ cursor: 'pointer' });
      spr.on('pointerdown', () => this.onHerbClick(h.id));
      this.herbsOnMap.set(h.id, spr);
    }
  }

  // ---------- NPC ----------
  private spawnNPCs() {
    for (const n of MAP.npcs) {
      const pos = this.isoToScreen(n.tx, n.ty);
      const spr = this.add.image(pos.x, pos.y - 20, n.icon);
      spr.setScale(0.6);
      spr.setDepth(pos.y + 10);
      spr.setInteractive({ cursor: 'pointer' });
      spr.on('pointerdown', () => this.onNpcClick(n));
      this.npcsOnMap.set(n.id, spr);
    }
  }

  // ---------- 输入 ----------
  private setupInput() {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.button !== 0) return;
      const iso = this.screenToIso(pointer.x, pointer.y);
      if (iso.tx < 0 || iso.tx >= MAP.width || iso.ty < 0 || iso.ty >= MAP.height) return;
      this.movePlayerTo(iso.tx, iso.ty);
    });
  }

  // ---------- 移动 ----------
  private movePlayerTo(tx: number, ty: number) {
    if (this.isMoving) return;
    const start = this.screenToIso(this.player.x, this.player.y + 20);
    this.pathfinder.findPath(start.tx, start.ty, tx, ty, (path) => {
      if (!path || path.length === 0) return;
      this.isMoving = true;
      this.walkPath(path);
    });
    this.pathfinder.calculate();
  }

  private walkPath(path: { x: number; y: number }[]) {
    let i = 0;
    const step = () => {
      if (i >= path.length) {
        this.isMoving = false;
        return;
      }
      const p = path[i];
      const pos = this.isoToScreen(p.x, p.y);
      const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y + 20, pos.x, pos.y);
      const duration = Math.min(dist / 2.5, 300);
      this.tweens.add({
        targets: this.player,
        x: pos.x,
        y: pos.y - 20,
        duration,
        ease: 'Linear',
        onUpdate: (tween: Phaser.Tweens.Tween) => {
          const progress = tween.progress;
          // 左右翻转
          const dir = pos.x - this.player.x;
          if (Math.abs(dir) > 2) this.player.setScale(dir > 0 ? 0.6 : -0.6, 0.6);
          // 行走上下晃动
          this.player.y += Math.sin(progress * Math.PI * 4) * 1.5;
          this.player.setDepth(this.player.y + 20 + 10);
        },
        onComplete: () => {
          this.player.setScale(0.6, 0.6);
          i++;
          step();
        },
      });
    };
    step();
  }

  // ---------- 交互 ----------
  private onHerbClick(herbId: string) {
    const herbData = HERB_MAP[herbId];
    if (!herbData) return;
    const spr = this.herbsOnMap.get(herbId);
    if (!spr || !spr.active) return;
    // 距离检查
    const playerIso = this.screenToIso(this.player.x, this.player.y + 20);
    const herb = MAP.herbs.find((h) => h.id === herbId)!;
    const dist = Math.abs(playerIso.tx - herb.tx) + Math.abs(playerIso.ty - herb.ty);
    if (dist > 1) {
      this.showDialog('太远了，走近些再采。');
      return;
    }
    // 采集动画
    this.tweens.add({
      targets: spr,
      scaleX: 0.5,
      scaleY: 0.5,
      alpha: 0,
      duration: 400,
      onComplete: () => {
        spr.destroy();
        this.herbsOnMap.delete(herbId);
        this.inventory.push(herbId);
        this.showDialog(`采到 ${herbData.name}！${herbData.collect.desc}`);
        this.updateInventoryUI();
        this.checkQuest();
        this.emitToParent('herbCollected', { herbId });
      },
    });
  }

  private onNpcClick(npc: MapData['npcs'][0]) {
    const playerIso = this.screenToIso(this.player.x, this.player.y + 20);
    const dist = Math.abs(playerIso.tx - npc.tx) + Math.abs(playerIso.ty - npc.ty);
    if (dist > 1) {
      this.showDialog('走近些再说话。');
      return;
    }
    if (npc.id === 'patient' && !this.questDone) {
      const count = this.inventory.filter((id) => id === QUEST_HERB_ID).length;
      if (count >= QUEST_HERB_COUNT) {
        this.showDialog('太好了！三株甘草都采齐了。谢谢你，小郎中。');
        this.questDone = true;
        this.emitToParent('questComplete', { npcId: npc.id });
      } else {
        this.showDialog(npc.dialog + `（还需 ${QUEST_HERB_COUNT - count} 株甘草）`);
      }
    } else {
      this.showDialog(npc.dialog);
    }
    this.emitToParent('npcTalk', { npcId: npc.id });
  }

  // ---------- UI ----------
  private buildUI() {
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;
    this.uiContainer = this.add.container(0, 0);
    this.uiContainer.setScrollFactor(0);
    this.uiContainer.setDepth(10000);

    // 国风配色常量
    const PAPER = 0xf7f1e3;
    const INK = 0x2a2622;
    const RED = 0x8e3a3a;
    const GOLD = 0xc8a070;

    // 左上：状态栏
    const statusBg = this.add.rectangle(10, 10, 180, 60, PAPER, 0.92).setOrigin(0);
    const statusText = this.add.text(20, 20, '医术值: 0\n等级: 学徒', {
      fontSize: '14px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
    });
    this.uiContainer.add([statusBg, statusText]);

    // 右上：小地图
    this.miniMap = this.add.graphics();
    this.miniMap.setPosition(W - 140, 10);
    this.drawMiniMap();
    this.uiContainer.add(this.miniMap);

    // 右下：功能按钮（朱红底+米白字）
    const btnDefs = [
      { label: '背包', x: W - 70, y: H - 160, action: () => this.toggleInventory() },
      { label: '本草', x: W - 70, y: H - 110, action: () => this.toggleHerbBook() },
      { label: '问诊', x: W - 70, y: H - 60, action: () => this.showDialog('点击病人即可问诊') },
    ];
    for (const b of btnDefs) {
      const bg = this.add.rectangle(b.x, b.y, 60, 40, RED, 0.85).setInteractive();
      const txt = this.add.text(b.x, b.y, b.label, {
        fontSize: '14px', color: '#f7f1e3', fontFamily: 'Noto Serif SC, serif',
      }).setOrigin(0.5);
      bg.on('pointerdown', b.action);
      this.uiContainer.add([bg, txt]);
    }

    // 任务面板（右上偏下）
    this.questPanel = this.add.container(W - 210, 130);
    const qBg = this.add.rectangle(0, 0, 180, 80, PAPER, 0.92).setOrigin(0);
    const qText = this.add.text(10, 10, '任务：采集甘草×3\n进度：0/3', {
      fontSize: '13px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
    });
    this.questPanel.add([qBg, qText]);
    this.uiContainer.add(this.questPanel);

    // 对话框（底部居中）
    this.dialogBox = this.add.container(W / 2, H - 60);
    const dBg = this.add.rectangle(0, 0, Math.min(600, W - 40), 80, PAPER, 0.95);
    const dText = this.add.text(0, 0, '', {
      fontSize: '15px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
      wordWrap: { width: Math.min(560, W - 80) }, align: 'center',
    }).setOrigin(0.5);
    this.dialogBox.add([dBg, dText]);
    this.dialogBox.setVisible(false);
    this.uiContainer.add(this.dialogBox);

    // 背包面板（默认隐藏）
    this.inventoryPanel = this.add.container(W / 2, H / 2);
    const iBg = this.add.rectangle(0, 0, 320, 400, PAPER, 0.95);
    const iTitle = this.add.text(0, -170, '药材背包', {
      fontSize: '18px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
    }).setOrigin(0.5);
    const iClose = this.add.text(140, -170, '×', {
      fontSize: '20px', color: '#8e3a3a', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    iClose.on('pointerdown', () => this.toggleInventory());
    this.inventoryPanel.add([iBg, iTitle, iClose]);
    this.inventoryPanel.setVisible(false);
    this.uiContainer.add(this.inventoryPanel);

    // 本草图鉴面板（默认隐藏）
    this.herbBookPanel = this.add.container(W / 2, H / 2);
    const hBg = this.add.rectangle(0, 0, 360, 440, PAPER, 0.95);
    const hTitle = this.add.text(0, -190, '本草图鉴', {
      fontSize: '18px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
    }).setOrigin(0.5);
    const hClose = this.add.text(160, -190, '×', {
      fontSize: '20px', color: '#8e3a3a', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    hClose.on('pointerdown', () => this.toggleHerbBook());
    this.herbBookPanel.add([hBg, hTitle, hClose]);
    this.herbBookPanel.setVisible(false);
    this.uiContainer.add(this.herbBookPanel);

    this.updateInventoryUI();
    this.updateHerbBookUI();
  }

  private drawMiniMap() {
    const g = this.miniMap;
    g.clear();
    const scale = 6;
    const ox = 10, oy = 10;
    for (let ty = 0; ty < MAP.height; ty++) {
      for (let tx = 0; tx < MAP.width; tx++) {
        const px = ox + tx * scale;
        const py = oy + ty * scale;
        const blocked = MAP.blocks.some(([bx, by]) => bx === tx && by === ty);
        g.fillStyle(blocked ? 0x555555 : 0x88aa88, 1);
        g.fillRect(px, py, scale - 1, scale - 1);
      }
    }
    // 玩家点
    const pIso = this.screenToIso(this.player.x, this.player.y + 20);
    g.fillStyle(0xffcc66, 1);
    g.fillRect(ox + pIso.tx * scale, oy + pIso.ty * scale, scale - 1, scale - 1);
  }

  private showDialog(text: string) {
    const dText = this.dialogBox.getAt(1) as Phaser.GameObjects.Text;
    dText.setText(text);
    this.dialogBox.setVisible(true);
    this.time.delayedCall(3500, () => this.dialogBox.setVisible(false));
  }

  toggleInventory() {
    this.inventoryPanel.setVisible(!this.inventoryPanel.visible);
  }

  toggleHerbBook() {
    this.herbBookPanel.setVisible(!this.herbBookPanel.visible);
  }

  private updateInventoryUI() {
    // 清除旧条目
    while (this.inventoryPanel.length > 3) {
      this.inventoryPanel.removeAt(3, true);
    }
    const counts: Record<string, number> = {};
    for (const id of this.inventory) counts[id] = (counts[id] || 0) + 1;
    let y = -130;
    for (const [id, count] of Object.entries(counts)) {
      const h = HERB_MAP[id];
      const row = this.add.text(-130, y, `${h.name} ×${count}  ${h.nature} ${h.flavor}`, {
        fontSize: '14px', color: '#2a2622', fontFamily: 'Noto Serif SC, serif',
      });
      this.inventoryPanel.add(row);
      y += 28;
    }
    if (Object.keys(counts).length === 0) {
      this.inventoryPanel.add(this.add.text(0, -100, '空空如也', {
        fontSize: '14px', color: '#888', fontFamily: 'Noto Serif SC, serif',
      }).setOrigin(0.5));
    }
  }

  private updateHerbBookUI() {
    while (this.herbBookPanel.length > 3) {
      this.herbBookPanel.removeAt(3, true);
    }
    let y = -150;
    for (const h of HERB_LIBRARY) {
      const owned = this.inventory.includes(h.id);
      const color = owned ? '#2a2622' : '#999';
      const row = this.add.text(-150, y, `${owned ? '✓' : '○'} ${h.name}  ${h.nature}${h.flavor}  ${h.effects.slice(0, 2).join('、')}`, {
        fontSize: '13px', color, fontFamily: 'Noto Serif SC, serif',
      });
      this.herbBookPanel.add(row);
      y += 26;
    }
  }

  private checkQuest() {
    const count = this.inventory.filter((id) => id === QUEST_HERB_ID).length;
    const qText = this.questPanel.getAt(1) as Phaser.GameObjects.Text;
    qText.setText(`任务：采集甘草×3\n进度：${count}/${QUEST_HERB_COUNT}`);
    if (this.questDone) {
      qText.setText('任务完成！\n咳嗽的老者已得到甘草。');
      qText.setColor('#3a7a3a');
    }
  }

  // ---------- 外部接口 ----------
  collectHerb(herbId: string) {
    this.onHerbClick(herbId);
  }

  talkTo(npcId: string) {
    const npc = MAP.npcs.find((n) => n.id === npcId);
    if (npc) this.onNpcClick(npc);
  }

  private emitToParent(event: string, data?: any) {
    (window as any).__tcmRpg?.emit?.(event, data);
  }

  private setupPostMessage() {
    // 初始化时发送 ready
    this.emitToParent('ready');
  }

  update() {
    // 每帧更新小地图玩家位置
    if (this.miniMap) this.drawMiniMap();
  }
}
